<template>
  <v-overlay
    v-model="visible"
    :persistent="true"
    :z-index="9999"
    :opacity="0.5"
    color="rgba(0, 0, 0, 0.5)"
    class="payment-banner-overlay"
  >
    <v-card
      class="payment-banner-card mx-auto"
      max-width="500"
      elevation="8"
      color="white"
    >
      <!-- Header -->
      <v-card-title
        class="payment-banner-header white--text pa-4 justify-center"
      >
        <v-icon color="white" class="mr-3"> mdi-alert-triangle </v-icon>
        <span class="text-h6 font-weight-bold">
          {{ $t('paymentBanner.title') }}
        </span>
      </v-card-title>

      <!-- Main Content -->
      <v-card-text class="pa-6">
        <div class="text-h6 font-weight-medium mb-2 text--primary">
          {{ $t('paymentBanner.blockMessage') }}
        </div>

        <div class="text-body-1 mb-4 text--secondary">
          {{
            $t('paymentBanner.settleMessage', {
              reason: paymentReason,
              date: formattedDueDate,
            })
          }}
        </div>

        <!-- Payment Details Box -->
        <v-card class="payment-details-box mb-4" flat outlined>
          <v-card-text class="pa-4">
            <div class="d-flex align-center mb-3">
              <v-icon color="error" class="mr-2"> mdi-clock-outline </v-icon>
              <span class="font-weight-medium text--primary">
                {{ $t('paymentBanner.dueDate') }}: {{ formattedDueDate }}
              </span>
            </div>

            <div class="d-flex align-center">
              <v-icon color="error" class="mr-2">
                mdi-credit-card-outline
              </v-icon>
              <span class="font-weight-medium text--primary">
                {{ $t('paymentBanner.outstandingAmount') }}:
                {{ toMoney(amount, { currency: 'USD' }) }}
              </span>
            </div>
          </v-card-text>
        </v-card>

        <!-- Retry the declined charge on the card on file (#30848) -->
        <client-charge-retry class="mb-4" @paid="handlePaid" />

        <!-- Action Buttons -->
        <div class="d-flex flex-column ga-3">
          <v-btn
            outlined
            large
            block
            color="error"
            class="py-3"
            @click="handlePayNow"
          >
            {{ $t('paymentBanner.payNow') }}
          </v-btn>

          <v-btn
            outlined
            large
            block
            color="primary"
            class="text-body-1 mt-2"
            @click="handleViewInvoice"
          >
            {{ $t('paymentBanner.viewInvoice') }}
          </v-btn>

          <v-btn
            text
            large
            block
            color="primary"
            class="text-body-1 primary--text mt-2"
            @click="handleContactSupport"
          >
            {{ $t('paymentBanner.contactSupport') }}
          </v-btn>
        </div>

        <!-- Footer Message -->
        <div class="text-caption text-center mt-4 text--secondary">
          {{ $t('paymentBanner.processingNote') }}
        </div>
      </v-card-text>
    </v-card>
  </v-overlay>
</template>

<script>
import FormattersMixin from 'components/mixins/formatters-mixin'
import ClientChargeRetry from 'components/common/client-charge-retry'

export default {
  name: 'PaymentBanner',
  components: { ClientChargeRetry },
  mixins: [FormattersMixin],
  props: ['show', 'dueDate', 'amount'],
  data() {
    return {
      visible: false,
    }
  },
  mounted() {
    document.body.style.setProperty('overflow', 'hidden', 'important')
  },
  computed: {
    daysToBlock() {
      const days = Math.ceil(
        (new Date(this.dueDate) - new Date()) / (1000 * 60 * 60 * 24)
      )
      return days
    },
    formattedDueDate() {
      if (!this.dueDate) {
        return ''
      }

      const date = new Date(this.dueDate)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    },
    paymentReason() {
      const { billing_info } = this.$store.state.app.configuration
      const reason = billing_info && billing_info.payment_issue_reason
      const reasonTexts = {
        declined: 'your payment was declined',
        missing_info: 'payment information is missing',
        being_processed: 'your payment is being processed',
        processing_error: 'a payment processing error occurred',
        could_not_process: 'your payment could not be processed',
      }
      return reasonTexts[reason] || 'a payment issue occurred'
    },
  },

  watch: {
    show: {
      handler(newVal) {
        this.visible = newVal
      },
      immediate: true,
    },
  },

  beforeDestroy() {
    document.body.style.removeProperty('overflow')
  },

  methods: {
    handlePayNow() {
      this.$emit('pay-now')
    },
    handlePaid() {
      this.$emit('paid')
    },
    handleViewInvoice() {
      this.$emit('view-invoice')
    },
    handleContactSupport() {
      this.$emit('contact-support')
    },
  },
}
</script>

<style lang="scss" scoped>
.payment-banner-overlay {
  background-color: rgba(0, 0, 0, 0.5) !important;

  .payment-banner-card {
    border-radius: 12px !important;
    overflow: hidden;
    background-color: white !important;
  }

  .payment-banner-header {
    background: linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%) !important;
    border-radius: 0 !important;
  }

  .payment-details-box {
    background-color: rgba(244, 67, 54, 0.08) !important;
    border: 1px solid rgba(244, 67, 54, 0.2) !important;
    border-radius: 8px !important;
  }
}

@media (max-width: 600px) {
  .payment-banner-overlay {
    .payment-banner-card {
      margin: 16px;
      max-width: calc(100% - 32px);
    }
  }
}
</style>
